import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCommercialProjectRow } from "./commercialProjectAcf";
import { addCommercialWorkOrderChild } from "./addProjectApi";
import { canAddCommercialWorkOrderChild } from "./commercialRoleAccess";
import { filterExpansionRowsForBase } from "./expansionGroupUtils";
import { isCvinExpansion } from "../projectGridColumns";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import {
  CONNECTION_OPTIONS,
  DEPARTMENT_OPTIONS,
  IP_OPTIONS,
  isRevenueCommercialProjectType,
  SERVICE_SPEED_OPTIONS,
  serviceSpeedLabel,
} from "./commercialProjectSchema";
import "./addProjectPage.css";

function readSummaryFromRow(row) {
  const acf = row?.acf_groups_json || {};
  return acf.project_summary || acf["Project Summary Test 2"] || {};
}

function resolveCustomerFromCommercialRow(row) {
  if (!row) return "";
  const summary = readSummaryFromRow(row);
  for (const value of [row.customer, summary.customer, summary.customer_name, row.name]) {
    const text = value != null ? String(value).trim() : "";
    if (text) return text;
  }
  return "";
}

function resolveGroupCustomerFromRows(parentWo, rows) {
  for (const row of rows || []) {
    const customer = resolveCustomerFromCommercialRow(row);
    if (customer) return customer;
  }
  return `Work order ${parentWo}`;
}

const EMPTY_LOCATION = {
  departments: [],
  service_location: "",
  connection_types: [],
  a_location: "",
  z_location: "",
  ips: "",
  service_speed: "",
  mrc: "",
  nrc: "",
  subtype: "",
  notes: "",
  site_contact_name: "",
  site_contact_email: "",
  site_contact_phone: "",
  it_contact: "",
  it_email: "",
  it_number: "",
};

function FormField({ label, children, hint }) {
  return (
    <div className="add-project-field">
      <label>{label}</label>
      {children}
      {hint ? (
        <span className="project-detail-muted" style={{ fontSize: 12 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function CheckboxMultiSelect({ label, options, value, onChange, disabled, required }) {
  const selected = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
  return (
    <FormField label={label}>
      <div className="add-project-departments commercial-multiselect">
        {options.map((opt) => (
          <label key={opt} className="commercial-multiselect-option">
            <input
              type="checkbox"
              checked={selected.has(opt)}
              disabled={disabled}
              required={required && selected.size === 0}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                onChange([...next]);
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    </FormField>
  );
}

function locationHasPtpOrDark(location) {
  const types = Array.isArray(location.connection_types) ? location.connection_types : [];
  return types.some((t) => t === "PTP" || t === "Dark");
}

function locationHasDia(location) {
  const types = Array.isArray(location.connection_types) ? location.connection_types : [];
  return types.includes("DIA");
}

function AddCommercialChildForm({ apiUrl, getAuthHeaders, parentWorkOrder, groupSource, groupCustomer, nextWorkOrderHint }) {
  const navigate = useNavigate();
  const [location, setLocation] = useState({ ...EMPTY_LOCATION });
  const [projectName, setProjectName] = useState("");
  const [workOrderOverride, setWorkOrderOverride] = useState("");
  const [signedServiceOrderFile, setSignedServiceOrderFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const projectType = groupSource?.project_type || readSummaryFromRow(groupSource).type || "Revenue";
  const isRevenue = isRevenueCommercialProjectType(projectType);
  const inheritedCustomer = groupCustomer || resolveGroupCustomerFromRows(parentWorkOrder, [groupSource]);

  const updateLocation = (patch) => {
    setLocation((prev) => ({ ...prev, ...patch }));
  };

  const validateForm = () => {
    if (!location.departments?.length) {
      return "Select at least one department.";
    }
    if (!isRevenue) return "";
    if (!String(location.service_location || "").trim()) {
      return "Service location is required.";
    }
    if (!location.connection_types?.length) {
      return "Select at least one service type.";
    }
    if (locationHasDia(location)) {
      if (!String(location.service_speed || "").trim()) {
        return "Service speed is required for DIA.";
      }
      if (!String(location.ips || "").trim()) {
        return "IPs selection is required for DIA.";
      }
    }
    if (locationHasPtpOrDark(location)) {
      if (!String(location.a_location || "").trim() || !String(location.z_location || "").trim()) {
        return "A and Z locations are required for PTP or Dark.";
      }
    }
    const woRaw = String(workOrderOverride || "").trim();
    if (woRaw) {
      const parent = String(parentWorkOrder || "").trim();
      const escapedParent = parent.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const fullOk = new RegExp(`^${escapedParent}\\.[0-9]+$`).test(woRaw);
      const dotSuffixOk = /^\.[0-9]+$/.test(woRaw);
      const suffixOnlyOk = /^[0-9]+$/.test(woRaw) && woRaw !== parent;
      if (!fullOk && !dotSuffixOk && !suffixOnlyOk) {
        return `Work order must be ${parent}.##, .##, or a numeric suffix.`;
      }
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        locations: [location],
        customer: inheritedCustomer,
        project_type: projectType,
      };
      if (String(projectName || "").trim()) {
        payload.project_name = String(projectName).trim();
      }
      if (String(workOrderOverride || "").trim()) {
        payload.work_order = String(workOrderOverride).trim();
      }
      const data = await addCommercialWorkOrderChild(parentWorkOrder, payload, {
        apiUrl,
        getAuthHeaders,
        signedServiceOrderFile: isRevenue ? signedServiceOrderFile : null,
      });
      const projectId = data?.project?.id;
      if (projectId != null) {
        navigate(`/project/${projectId}`);
        return;
      }
      navigate(`/work-order/${parentWorkOrder}`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not add sub-work order.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="add-project-form" onSubmit={(e) => void submit(e)}>
      <section className="add-project-section">
        <h3 className="add-project-section-title">Parent work order</h3>
        <p className="project-detail-muted" style={{ margin: 0, fontSize: 14 }}>
          Leave work order blank to use the next available suffix under <strong>{parentWorkOrder}</strong>
          {nextWorkOrderHint ? (
            <>
              {" "}
              (next: <strong>{nextWorkOrderHint}</strong>)
            </>
          ) : null}
          .
        </p>
        <dl className="parent-wo-details" style={{ marginTop: 12 }}>
          <div>
            <dt>Parent</dt>
            <dd>{parentWorkOrder}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{projectType}</dd>
          </div>
        </dl>
      </section>

      <section className="add-project-section">
        <h3 className="add-project-section-title">Sub-work order details</h3>
        <FormField
          label="Work order (optional)"
          hint={`Examples: ${parentWorkOrder}.51, .51, or 51. Must not already exist.`}
        >
          <input
            type="text"
            value={workOrderOverride}
            disabled={saving}
            placeholder={nextWorkOrderHint || `${parentWorkOrder}.##`}
            onChange={(e) => setWorkOrderOverride(e.target.value)}
          />
        </FormField>
        <FormField label="Project name (optional)" hint="Defaults to customer and service location.">
          <input
            type="text"
            value={projectName}
            disabled={saving}
            onChange={(e) => setProjectName(e.target.value)}
          />
        </FormField>
        <CheckboxMultiSelect
          label="Departments"
          options={DEPARTMENT_OPTIONS}
          value={location.departments}
          disabled={saving}
          required
          onChange={(v) => updateLocation({ departments: v })}
        />
        <FormField label="Service location">
          <input
            type="text"
            value={location.service_location}
            required={isRevenue}
            disabled={saving}
            onChange={(e) => updateLocation({ service_location: e.target.value })}
          />
        </FormField>
        {isRevenue ? (
          <FormField label="Subtype">
            <select
              value={location.subtype || ""}
              disabled={saving}
              onChange={(e) => updateLocation({ subtype: e.target.value })}
            >
              <option value="">— Select subtype —</option>
              {["Upgrade", "Renewal", "New"].map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </FormField>
        ) : null}
        <FormField label="Notes">
          <textarea
            value={location.notes || ""}
            disabled={saving}
            rows={3}
            placeholder="Optional notes for this location"
            onChange={(e) => updateLocation({ notes: e.target.value })}
          />
        </FormField>
        <FormField label="Site contact name">
          <input
            type="text"
            value={location.site_contact_name}
            disabled={saving}
            onChange={(e) => updateLocation({ site_contact_name: e.target.value })}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="Site contact email">
            <input
              type="email"
              value={location.site_contact_email}
              disabled={saving}
              onChange={(e) => updateLocation({ site_contact_email: e.target.value })}
            />
          </FormField>
          <FormField label="Site contact phone">
            <input
              type="tel"
              value={location.site_contact_phone}
              disabled={saving}
              onChange={(e) => updateLocation({ site_contact_phone: e.target.value })}
            />
          </FormField>
        </div>
        <FormField label="IT contact name">
          <input
            type="text"
            value={location.it_contact}
            disabled={saving}
            onChange={(e) => updateLocation({ it_contact: e.target.value })}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="IT email">
            <input
              type="email"
              value={location.it_email}
              disabled={saving}
              onChange={(e) => updateLocation({ it_email: e.target.value })}
            />
          </FormField>
          <FormField label="IT phone">
            <input
              type="tel"
              value={location.it_number}
              disabled={saving}
              onChange={(e) => updateLocation({ it_number: e.target.value })}
            />
          </FormField>
        </div>
        {isRevenue ? (
          <>
            <CheckboxMultiSelect
              label="Service types"
              options={CONNECTION_OPTIONS}
              value={location.connection_types}
              disabled={saving}
              required
              onChange={(v) => updateLocation({ connection_types: v })}
            />
            {locationHasDia(location) ? (
              <div className="add-project-row">
                <FormField label="Service speed">
                  <select
                    value={location.service_speed}
                    required
                    disabled={saving}
                    onChange={(e) => updateLocation({ service_speed: e.target.value })}
                  >
                    <option value="">— Select speed —</option>
                    {SERVICE_SPEED_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="IPs">
                  <select
                    value={location.ips}
                    required
                    disabled={saving}
                    onChange={(e) => updateLocation({ ips: e.target.value })}
                  >
                    <option value="">— Select —</option>
                    {IP_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        /{opt}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
            ) : null}
            {locationHasPtpOrDark(location) ? (
              <div className="add-project-row">
                <FormField label="A location">
                  <input
                    type="text"
                    value={location.a_location}
                    required
                    disabled={saving}
                    onChange={(e) => updateLocation({ a_location: e.target.value })}
                  />
                </FormField>
                <FormField label="Z location">
                  <input
                    type="text"
                    value={location.z_location}
                    required
                    disabled={saving}
                    onChange={(e) => updateLocation({ z_location: e.target.value })}
                  />
                </FormField>
              </div>
            ) : null}
            <div className="add-project-row">
              <FormField label="MRC">
                <input
                  type="text"
                  inputMode="decimal"
                  value={location.mrc}
                  disabled={saving}
                  onChange={(e) => updateLocation({ mrc: e.target.value })}
                />
              </FormField>
              <FormField label="NRC">
                <input
                  type="text"
                  inputMode="decimal"
                  value={location.nrc}
                  disabled={saving}
                  onChange={(e) => updateLocation({ nrc: e.target.value })}
                />
              </FormField>
            </div>
            <section className="add-project-section" style={{ padding: 0, border: "none" }}>
              <h3 className="add-project-section-title">Service order</h3>
              <FormField label="Upload service order (PDF)" hint="Optional.">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  disabled={saving}
                  onChange={(e) => setSignedServiceOrderFile(e.target.files?.[0] || null)}
                />
              </FormField>
            </section>
          </>
        ) : (
          <p className="project-detail-muted" style={{ margin: 0, fontSize: 13 }}>
            MRC, NRC, term, and circuit details are not used for Non-Revenue or Capital projects.
          </p>
        )}
      </section>

      {error ? (
        <p className="project-detail-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="add-project-actions">
        <button type="submit" className="add-project-submit" disabled={saving}>
          {saving ? "Adding…" : "Add sub-work order"}
        </button>
        <Link to={`/work-order/${parentWorkOrder}`} className="project-detail-muted">
          Cancel
        </Link>
      </div>
    </form>
  );
}

export default function AddCommercialChildPage({ apiUrl, getAuthHeaders }) {
  const { base } = useParams();
  const parentWorkOrder = String(base || "").trim();
  const viewerEmail = getViewerEmailFromStorage();
  const allowed = canAddCommercialWorkOrderChild(viewerEmail);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupSource, setGroupSource] = useState(null);
  const [groupCustomer, setGroupCustomer] = useState("");
  const [nextWorkOrderHint, setNextWorkOrderHint] = useState("");
  const [expansionAddPath, setExpansionAddPath] = useState("");

  const computeNextWorkOrderHint = useCallback((parentWo, matched) => {
    let maxSuffix = 0;
    let suffixWidth = 0;
    for (const row of matched || []) {
      const wo = String(row.work_order || "").trim();
      if (!wo.startsWith(`${parentWo}.`)) continue;
      const suffixPart = wo.slice(parentWo.length + 1);
      if (!/^\d+$/.test(suffixPart)) continue;
      const suffix = parseInt(suffixPart, 10);
      if (Number.isFinite(suffix) && suffix > maxSuffix) maxSuffix = suffix;
      if (suffixPart.length > suffixWidth) suffixWidth = suffixPart.length;
    }
    const nextSuffix = String(maxSuffix + 1).padStart(Math.max(suffixWidth, 2), "0");
    return `${parentWo}.${nextSuffix}`;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = apiUrl || getApiBase();
      const { data } = await axios.get(`${url}/projects`, { headers: getAuthHeaders() });
      const allProjects = Array.isArray(data) ? data : [];
      const expansionMatched = filterExpansionRowsForBase(allProjects.filter(isCvinExpansion), parentWorkOrder);
      const hasExpansionSubsegment = expansionMatched.some((row) => {
        const wo = String(row.work_order || "").trim();
        return wo.includes(".");
      });
      if (hasExpansionSubsegment) {
        setExpansionAddPath(`/expansion/segment/${encodeURIComponent(parentWorkOrder)}/add`);
        setGroupSource(null);
        setError("");
        return;
      }
      setExpansionAddPath("");
      const all = allProjects.filter(isCommercialProjectRow);
      const matched = all.filter((row) => {
        const rowBase =
          row.work_order_base != null && String(row.work_order_base).trim() !== ""
            ? String(row.work_order_base).trim()
            : String(row.work_order ?? "").split(".")[0];
        return rowBase === parentWorkOrder;
      });
      const parentShell = matched.find((row) => String(row.work_order || "").trim() === parentWorkOrder);
      const children = matched.filter((row) =>
        String(row.work_order || "").startsWith(`${parentWorkOrder}.`)
      );
      const source = parentShell || children[0] || null;
      if (!source) {
        setError(`No commercial project found for work order ${parentWorkOrder}.`);
        setGroupSource(null);
        return;
      }
      setGroupSource(source);
      setGroupCustomer(resolveGroupCustomerFromRows(parentWorkOrder, matched));
      setNextWorkOrderHint(computeNextWorkOrderHint(parentWorkOrder, matched));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load parent work order.");
      setGroupSource(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, computeNextWorkOrderHint, getAuthHeaders, parentWorkOrder]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!allowed) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-muted">
            Adding sub-work orders is limited to Coordinators, Permitting staff, and Administrators.
          </p>
          <Link to={`/work-order/${parentWorkOrder}`}>Back to work order {parentWorkOrder}</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-muted">Loading work order {parentWorkOrder}…</p>
        </div>
      </div>
    );
  }

  if (expansionAddPath) {
    return <Navigate to={expansionAddPath} replace />;
  }

  if (error || !groupSource) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-error">{error || "Parent work order not found."}</p>
          <Link to="/">Back to board</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="add-project-page">
      <div className="add-project-page-inner">
        <Link to={`/work-order/${parentWorkOrder}`} className="project-detail-back">
          ← Back to work order {parentWorkOrder}
        </Link>
        <h2 className="add-project-title">Add sub-work order</h2>
        <p className="add-project-subtitle">
          Add another entry under parent work order {parentWorkOrder}. Use this for ongoing commercial
          orders such as general fiber incidents where new incidents are tracked as sub-work orders over
          time.
        </p>
        <AddCommercialChildForm
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          parentWorkOrder={parentWorkOrder}
          groupSource={groupSource}
          groupCustomer={groupCustomer}
          nextWorkOrderHint={nextWorkOrderHint}
        />
      </div>
    </div>
  );
}

export function AddCommercialChildGate(props) {
  return <AddCommercialChildPage {...props} />;
}
